import React, { memo, ReactNode, useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { SideNavContext } from './SideNav.context'
import { sideNavEntryStyles } from './sideNavEntryStyles'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  children: ReactNode
}

/**
 * Same rail as apps/dashboard's `SideMenu`: grey surface, hairline right rule. Sections
 * (`SideNavGroup`) stack from the top and draw their own bottom rules; the collapse toggle is
 * pinned to the foot of the rail. Groups and items read the collapsed state from
 * `SideNavContext` and drop their labels to leave a 60px icon strip. The GigSafe logo lives in
 * `TopNav`.
 *
 * Rendered by a page but displayed in `AdminShell`'s left column via a portal, so the rail runs
 * the full height under the top bar. Stays in the page's React tree, so route state, context and
 * the shell error boundary all still apply to it.
 */
const SideNavComponent = ({ children }: Props) => {
  const { slot, isCollapsed, toggleCollapsed, setHasSideNav } = useContext(SideNavContext)

  // Tells the shell a rail exists. A route change remounts the rail; the unmount/mount pair
  // lands in one commit, so the flag never flickers.
  useEffect(() => {
    setHasSideNav(true)

    return () => {
      setHasSideNav(false)
    }
  }, [setHasSideNav])

  if (!slot) {
    return null
  }

  const toggleLabel = isCollapsed ? 'Expand' : 'Collapse'
  // Two glyphs, one per state: the open sidebar while expanded, the hidden one while collapsed —
  // so the button previews what a click does. Same 20px well as a `SideNavItem` icon.
  const toggleIcon = (
    <span css={tw`flex h-5 w-5 items-center justify-center text-shades-1000`}>
      {isCollapsed ? (
        <GSIcon iconName="gs_sidebar_hidden_left_wide" size={20} />
      ) : (
        <GSIcon iconName="gs_sidebar" size={20} />
      )}
    </span>
  )

  return createPortal(
    <nav
      aria-label="Main"
      data-testid="admin-side-nav"
      data-collapsed={isCollapsed ? 'true' : 'false'}
      css={[
        // Width animates between the two rail sizes; `overflow-x-hidden` keeps labels from
        // spilling out mid-transition, and `motion-reduce` honours the OS "reduce motion" setting.
        tw`flex shrink-0 flex-col overflow-x-hidden overflow-y-auto border-0 border-r border-solid border-shades-200 bg-shades-100 transition-[width] duration-200 ease-in-out motion-reduce:transition-none`,
        isCollapsed ? tw`w-[60px]` : tw`w-[240px]`,
      ]}
    >
      {children}
      {/* `mt-auto` pins the toggle to the foot of the rail, under whatever the page put above. Same
          geometry as a `SideNavItem`, so its icon holds still through the animation too. */}
      <div css={tw`mt-auto shrink-0 border-0 border-t border-solid border-shades-200 px-3 py-3`}>
        <Tooltip title={isCollapsed ? toggleLabel : ''} placement="right">
          <span css={tw`flex`}>
            <Button
              size="small"
              color="grey"
              className={sideNavEntryStyles(isCollapsed)}
              leftIcon={toggleIcon}
              aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-expanded={!isCollapsed}
              onClick={toggleCollapsed}
              data-testid="admin-side-nav-toggle"
            >
              {isCollapsed ? undefined : toggleLabel}
            </Button>
          </span>
        </Tooltip>
      </div>
    </nav>,
    slot,
  )
}

export const SideNav = memo(SideNavComponent)
